import React from 'react';
import { useNavigate } from 'react-router-dom';
import PageBar from './PageBar';
import { DeskFrame, PhoneFrame } from './AppFrame';
import { useAuth } from './AuthContext';
import { menuFor } from '../lib/menu';
import useMediaQuery from '../hooks/useMediaQuery';
import { DESKTOP_QUERY } from './PageBar';

/*
  The shell around a screen that has stopped, when the app itself has not.

  **Built 10 September 2026 for job 25, and it exists because of rule 35.**
  Approved 5 September 2026 with Group H: whether a stopped screen keeps the
  menu depends on whether OnHand is still standing.

    The app is not standing    no shell. Opening OnHand, because nobody is
                               known yet and a bar describing you cannot be
                               drawn. OnHand has stopped, because the crash may
                               have taken the shell with it. Being signed out.

    The app is fine, this      the shell stays. Page not found, not yours to
    page is not                open, somebody has this count open.

  **Taking the menu away turns a wrong turn into a dead end.** That is exactly
  what the running app did on the count sheet somebody else has open: a card on
  a blank background with a Go Back button and nothing else.

  **The menu here is the app's one menu**, `src/lib/menu.jsx`, the same one the
  four dashboards draw. It is not a copy. A second menu is how OnHand ended up
  with six of them, drifted apart, calling the same screen two different names,
  which is the fault "One shell, one menu, three homes" was written to kill.

  **How a menu click gets anywhere from here.** Navigation inside OnHand is not
  addresses, it is a tab held in the browser's session store, one key per job
  level. So a click writes the tab the same way the dashboard's own `setTab`
  does, and then goes to the home address, where the dashboard reads it back.
  That is why `TAB_KEY` below has to agree with the four dashboards: if it ever
  disagrees, a menu click lands on whatever tab was last open instead of the one
  that was pressed, and nothing on screen would say so.

  **The page row stays and nothing in it is lit, which is the drawing's own
  answer and not the obvious one.** The first build of this file left the page
  row out, reasoning that a stopped page is not one of the pages. Laying the
  picture beside `docs/design/group-h/notfound-1440.png` (deleted 12 September 2026; kept in the tag before-phase-3-cleanup) showed the drawing
  keeps the row, and it is right: the row is the fastest way back to the work,
  and taking it away shrinks the way out to five area tabs on the exact screen
  where somebody is already lost. **Nothing is lit** because lighting a page
  would be a lie about where they are. Rule 35 gives the menu as the way OUT,
  not as a claim about where you are now.

  **Which pages, then.** The ones for the area they were last in, read from the
  same session key the dashboard writes. Somebody who mistypes an address while
  working in Ordering gets Ordering's pages back, rather than being handed a set
  they were not using. If nothing has been visited yet, the first area's.

  **And the band names the state**, "Not found", the way every other page in
  OnHand puts its name there. A screen with an empty band reads as a screen that
  failed to load rather than as an answer.
*/

const TAB_KEY = {
  admin: 'adminTab',
  manager: 'managerTab',
  rep: 'repTab',
  buyer: 'buyerTab',
};

/*
  **Home, for real** (the audit after the rebrand, issue 27, 18 September
  2026). "Go to Home" on Page not found went to the address of the dashboard,
  and the dashboard opens on the page it remembers, so the person landed back
  wherever they had last been. This writes Home as the page to open first,
  where that job level's dashboard looks for it.
*/
export function useGoHome() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  return () => {
    try { sessionStorage.setItem(TAB_KEY[profile?.role] || 'repTab', 'home'); } catch { /* refused: the dashboard's own first page still opens */ }
    navigate('/');
  };
}

const ROLE_LABEL = {
  admin: 'Administrator',
  manager: 'Manager',
  // The words every dashboard uses (the audit after the rebrand, issue 17).
  rep: 'Sales Rep',
  buyer: 'Buyer',
};

export default function SystemShell({ band, under, children }) {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(DESKTOP_QUERY);

  const role = profile?.role || 'rep';
  const areas = menuFor(role, {});


  /* The area they were last in, so the page row hands back the pages they were
     actually using. `let` rather than a lookup inside the render, because a
     browser that refuses session storage throws on the read as readily as on
     the write. */
  let lastTab = null;
  try { lastTab = sessionStorage.getItem(TAB_KEY[role] || 'repTab'); } catch { /* refused: fall through to the first area */ }
  const lastArea = areas.find(a => a.pages.some(p => p.key === lastTab)) || areas[0];
  /*
    **A screen with no band of its own is named after the page it is on the way
    to.** Added for the loading state: the band said "Loading", which is the
    spinner's job said twice, and Group H's drawing names the page instead. It
    is the same reason "Loading..." came off the opening screen.
  */
  const pageNow = lastArea && lastArea.pages.find(p => p.key === lastTab);
  const bandNow = band || pageNow?.label || lastArea?.label || '';

  /* Write the tab where the dashboard for this job level looks for it, then go
     home. The dashboard reads it on mount, so the person lands on the page they
     pressed rather than on the one they had open before they got lost. */
  function go(pageKey) {
    try { sessionStorage.setItem(TAB_KEY[role] || 'repTab', pageKey); } catch { /* a browser refusing session storage is not a reason to refuse to navigate */ }
    navigate('/');
  }

  /* The navigation the frames draw (rebrand step 4). Nothing is lit, because a
     stopped screen is not one of the pages, and every way in goes through `go`
     above, exactly as the old bar's tabs did. */
  const nav = {
    areas, activeKey: null, onSelect: go,
    userName: profile?.full_name, email: profile?.email,
    roleLabel: ROLE_LABEL[role] || 'Sales Rep', onSignOut: signOut,
  };

  const desktop = (
    <DeskFrame contentClassName="sy-content" nav={nav} fill>
      {/* A stop card centres in what it is given, so what it is given has to
          be the rest of the screen. See `.sy-wrap` in index.css. */}
      <PageBar title={bandNow} subtitle={under} />
      {children}
    </DeskFrame>
  );

  /* Below 1024 the page is named in place too: the bar no longer carries a
     page name, so the band is drawn at the top of the page, as on a desk. */
  const held = (
    <PhoneFrame nav={nav} fill>
      <PageBar title={bandNow} subtitle={under} />
      {children}
    </PhoneFrame>
  );

  /* Only one tree is mounted, never both hidden by CSS. The dashboards learned
     that on 5 September: a hidden tree still renders, and its pages still
     portalled their controls into the visible bar, so a page reachable from
     both trees put its control band in there twice. */
  return (
    <>
      <div className="rep-desktop-only">{isDesktop && desktop}</div>
      <div className="rep-mobile-only">{!isDesktop && held}</div>
    </>
  );
}
